import { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import api from '../../utils/api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { formatDateDMY } from '../../utils/dateFormat';
import DocumentPrintView from '../procurement/DocumentPrintView';

/**
 * Per-Supplier Ledger — mirrors the customer-ledger pattern.
 *  • Debits  = approved/received POs (in supplier currency) on the PO date.
 *  • Credits = supplier-payment line amounts (in supplier currency) on the
 *    payment received_date.
 *  • Running balance is single-currency (the supplier's primary currency).
 *
 * Lives as a tab inside the Supplier Payments page.
 *
 * Toggle "Pending POs only" hides paid POs and all payments so finance
 * gets a clean "what's still owed" picture per supplier.
 */
const SupplierLedgerTab = ({ suppliers }) => {
  const { companySettings, formatAmount } = useCurrency();

  const [supplierId, setSupplierId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [printing, setPrinting] = useState(false);

  const selectedSupplier = useMemo(
    () => suppliers.find((s) => s.id === supplierId) || null,
    [supplierId, suppliers],
  );

  useEffect(() => {
    if (!supplierId) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ supplier_id: supplierId });
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);
        if (pendingOnly) params.set('pending_only', 'true');
        setErr('');
        setLoading(true);
        const res = await api.get(`/supplier-payments/reports/supplier-ledger?${params.toString()}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || 'Failed to load ledger');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [supplierId, startDate, endDate, pendingOnly]);

  /* ── Excel export ───────────────────────────────────────────────── */
  const exportExcel = () => {
    if (!data || !data.entries?.length) return alert('No ledger entries to export.');
    const ccy = data.supplier_currency || '';
    const rows = [
      { Date: '', Type: 'Opening Balance', Reference: '', Description: '', [`Debit (${ccy})`]: '', [`Credit (${ccy})`]: '', [`Balance (${ccy})`]: Number((data.opening_balance || 0).toFixed(3)) },
      ...data.entries.map((e) => ({
        Date: e.date,
        Type: e.type === 'po' ? 'Purchase Order' : 'Payment',
        Reference: e.reference,
        Description: e.description,
        [`Debit (${ccy})`]: e.debit ? Number(e.debit.toFixed(3)) : '',
        [`Credit (${ccy})`]: e.credit ? Number(e.credit.toFixed(3)) : '',
        [`Balance (${ccy})`]: Number((e.balance || 0).toFixed(3)),
      })),
      { Date: '', Type: 'Totals', Reference: '', Description: '',
        [`Debit (${ccy})`]: Number((data.total_debit || 0).toFixed(3)),
        [`Credit (${ccy})`]: Number((data.total_credit || 0).toFixed(3)),
        [`Balance (${ccy})`]: Number((data.closing_balance || 0).toFixed(3)) },
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Supplier Ledger');
    const safe = (data.supplier_name || 'supplier').replace(/[^A-Za-z0-9_-]+/g, '_');
    XLSX.writeFile(wb, `supplier_ledger_${safe}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  /* ── PDF export — reuse DocumentPrintView (statement layout) so the
     Supplier Statement of Account gets the same repeating letterhead,
     footer banner, and table-header pagination as the Customer Ledger.
     The heavy lifting (multi-page @page margins, fixed corner banners,
     thead as table-header-group, page-break-inside on rows) lives in
     DocumentPrintView.jsx. */
  const exportPDF = () => {
    if (!data || !data.entries?.length) return alert('No ledger entries to export.');
    setPrinting(true);
  };

  return (
    <div data-testid="supplier-ledger-tab">
      <div className="bg-white border rounded-lg p-3 mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[220px]">
          <label className="block text-xs font-semibold text-slate-700 mb-1">Supplier *</label>
          <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className="border rounded px-2 py-1.5 text-sm w-full" data-testid="sl-supplier">
            <option value="">— Choose supplier —</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}{s.currency ? ` (${s.currency})` : ''}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border rounded px-2 py-1.5 text-sm" data-testid="sl-start" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border rounded px-2 py-1.5 text-sm" data-testid="sl-end" />
        </div>
        <label className="flex items-center gap-1 text-sm text-slate-700">
          <input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} data-testid="sl-pending-only" />
          Pending POs only
        </label>
        <div className="ml-auto flex gap-2">
          <button onClick={exportExcel} disabled={!data?.entries?.length} className="px-3 py-1.5 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-sm disabled:opacity-40" data-testid="sl-excel">📊 Excel</button>
          <button onClick={exportPDF} disabled={!data?.entries?.length} className="px-3 py-1.5 rounded border border-rose-600 text-rose-700 hover:bg-rose-50 text-sm disabled:opacity-40" data-testid="sl-pdf">📄 PDF / Print</button>
        </div>
      </div>

      {!supplierId && (
        <div className="bg-white border rounded-lg p-6 text-center text-slate-500 text-sm">
          Pick a supplier to view their statement of account.
        </div>
      )}

      {supplierId && loading && (
        <div className="bg-white border rounded-lg p-6 text-center text-slate-500 text-sm">Loading…</div>
      )}

      {supplierId && err && (
        <div className="bg-rose-50 border border-rose-300 rounded-lg p-4 text-sm text-rose-700">{err}</div>
      )}

      {supplierId && !loading && data && (
        <>
          {/* Summary strip */}
          <div className="grid sm:grid-cols-4 gap-3 mb-3">
            <div className="bg-slate-50 border rounded-lg p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Supplier</div>
              <div className="text-sm font-semibold text-slate-900">{data.supplier_name}</div>
              <div className="text-xs text-slate-500">Currency: {data.supplier_currency || '—'}</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="sl-opening">
              <div className="text-[11px] uppercase tracking-wide text-amber-700">Opening</div>
              <div className="text-base font-mono font-semibold">{(data.opening_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {data.supplier_currency || ''}</div>
            </div>
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
              <div className="text-[11px] uppercase tracking-wide text-sky-700">Debit / Credit</div>
              <div className="text-sm font-mono">
                +{(data.total_debit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
                {' '}/{' '}
                -{(data.total_credit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
              </div>
              <div className="text-xs text-slate-500">in {data.supplier_currency || ''}</div>
            </div>
            <div className={`border rounded-lg p-3 ${(data.closing_balance || 0) > 0 ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="sl-closing">
              <div className="text-[11px] uppercase tracking-wide text-slate-600">Closing</div>
              <div className={`text-lg font-mono font-bold ${(data.closing_balance || 0) > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                {(data.closing_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {data.supplier_currency || ''}
              </div>
              <div className="text-[11px] text-slate-500">{(data.closing_balance || 0) > 0 ? 'You owe the supplier' : 'Supplier owes you / settled'}</div>
            </div>
          </div>

          {/* Ledger table */}
          <div className="bg-white border rounded-lg overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Reference</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-right">Debit</th>
                  <th className="px-3 py-2 text-right">Credit</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t bg-amber-50/50">
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 italic text-slate-600" colSpan={3}>Opening Balance</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{(data.opening_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                </tr>
                {data.entries.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No entries for this period.</td></tr>
                )}
                {data.entries.map((e, i) => (
                  <tr key={i} className={`border-t ${e.type === 'po' ? 'hover:bg-sky-50/30' : 'hover:bg-emerald-50/30'}`} data-testid={`sl-row-${i}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateDMY(e.date)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${e.type === 'po' ? 'bg-sky-100 text-sky-800' : 'bg-emerald-100 text-emerald-800'}`}>
                        {e.type === 'po' ? 'PO' : 'Payment'}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-medium">{e.reference}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{e.description}</td>
                    <td className="px-3 py-2 text-right font-mono text-sky-700">{e.debit ? e.debit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 }) : ''}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700">{e.credit ? e.credit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 }) : ''}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{(e.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t bg-cyan-50 font-bold">
                  <td className="px-3 py-2" colSpan={4}>Totals · Closing Balance</td>
                  <td className="px-3 py-2 text-right font-mono">{(data.total_debit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}</td>
                  <td className="px-3 py-2 text-right font-mono">{(data.total_credit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}</td>
                  <td className="px-3 py-2 text-right font-mono">{(data.closing_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {data.supplier_currency || ''}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {selectedSupplier && (
            <div className="mt-3 text-xs text-slate-500">
              Showing approved/received POs only. Cancelled & draft POs are excluded.
              {selectedSupplier.payment_terms ? ` · Payment Terms: ${selectedSupplier.payment_terms}` : ''}
              {' '}· Company: {companySettings?.name || '—'} ({formatAmount(0, { showSymbol: true }).replace(/[\d.,\s-]/g, '')})
            </div>
          )}
        </>
      )}

      {/* Printable Supplier Statement of Account — uses the shared
          DocumentPrintView so pagination/letterhead behavior matches
          the Customer Ledger. */}
      {printing && data && (() => {
        const ccy = data.supplier_currency || '';
        const periodLabel = (startDate && endDate)
          ? `Period: ${formatDateDMY(startDate)} → ${formatDateDMY(endDate)}`
          : 'All-time';
        const itemColumns = pendingOnly
          ? [
              { key: 'date', label: 'Date' },
              { key: 'reference', label: 'PO #' },
              { key: 'description', label: 'Description' },
              { key: 'debit', label: `Amount Due (${ccy})`, align: 'right', numeric: true },
            ]
          : [
              { key: 'date', label: 'Date' },
              { key: 'type', label: 'Type' },
              { key: 'reference', label: 'Reference' },
              { key: 'description', label: 'Description' },
              { key: 'debit', label: `Debit (${ccy})`, align: 'right', numeric: true },
              { key: 'credit', label: `Credit (${ccy})`, align: 'right', numeric: true },
              { key: 'balance', label: `Balance (${ccy})`, align: 'right', numeric: true },
            ];
        const mappedEntries = (data.entries || []).map((e) => ({
          ...e,
          date: e.date ? formatDateDMY(e.date) : '',
          type: e.type === 'po' ? 'PO' : 'Payment',
        }));
        const items = pendingOnly
          ? mappedEntries
          : [
              {
                date: '',
                type: '',
                reference: '',
                description: 'Opening Balance',
                debit: 0,
                credit: 0,
                balance: data.opening_balance || 0,
              },
              ...mappedEntries,
            ];
        const totals = pendingOnly
          ? [
              { label: 'Total Outstanding', value: `${(data.total_debit || 0).toFixed(3)} ${ccy}`, bold: true },
            ]
          : [
              { label: 'Total Debit', value: (data.total_debit || 0).toFixed(3) },
              { label: 'Total Credit', value: (data.total_credit || 0).toFixed(3) },
              { label: 'Closing Balance', value: `${(data.closing_balance || 0).toFixed(3)} ${ccy}`, bold: true },
            ];
        return (
          <DocumentPrintView
            title={pendingOnly ? 'Outstanding POs Statement' : 'Supplier Statement of Account'}
            docNumber=""
            docDate={endDate || new Date().toISOString().split('T')[0]}
            headerLayout="statement"
            centerHeading={pendingOnly ? 'Outstanding POs Statement' : 'Supplier Statement of Account'}
            company={companySettings}
            customer={{
              name: data.supplier_name,
              address: selectedSupplier?.address,
              phone: selectedSupplier?.phone,
              email: selectedSupplier?.email,
              vat_reg_no: selectedSupplier?.vat_reg_no || selectedSupplier?.tax_id,
              period_label: periodLabel,
            }}
            parties={[]}
            meta={[]}
            itemColumns={itemColumns}
            items={items}
            totals={totals}
            onClose={() => setPrinting(false)}
          />
        );
      })()}
    </div>
  );
};

export default SupplierLedgerTab;
